import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  useColorModeValue,
} from '@chakra-ui/react';

const DataProcessingAddendum = () => {
  const bgColor = useColorModeValue('white', 'gray.800');

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg="linear-gradient(120deg, #14B7D6 0%, #1E9BE8 46%, #2E7CF0 100%)"
        color="white"
        pt={{ base: '130px', lg: '190px' }}
        pb={{ base: 12, lg: 16 }}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Heading size="xl" fontWeight="800">
              Cloud Data Processing Addendum
            </Heading>
            <Text fontSize="lg" opacity={0.95}>
              Google Cloud Platform - Customers
            </Text>
            <Text fontSize="sm" opacity={0.9}>
              Last Modified: September 20, 2022
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={12}>
        <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
          <VStack align="stretch" spacing={6}>
            <Box>
              <Text fontSize="md" lineHeight="tall" color="gray.700">
                This Cloud Data Processing Addendum including its appendices ("Addendum") is incorporated into the Agreement(s) under which Google has agreed to provide Google Cloud Platform, Google Workspace, or Cloud Identity (each as defined below), as applicable (the "Services"), to Customer.
              </Text>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                1. Commencement
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700">
                This Addendum will be effective and replace any terms previously applicable to the processing of Customer Data, including any Data Processing and Security Terms or Data Processing Amendment, from the Addendum Effective Date.
              </Text>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                2. Definitions
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700" mb={3}>
                Capitalized terms used but not defined in this Addendum have the meaning given to them in the Agreement:
              </Text>
              <VStack align="stretch" spacing={3} pl={4}>
                <Text fontSize="sm" color="gray.600">
                  <strong>Account:</strong> Means Customer's Google Cloud Platform account, Google Workspace account or Cloud Identity account, as applicable.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Customer Data:</strong> Data provided by or on behalf of Customer or its End Users via Google Cloud Platform, Google Workspace, or Cloud Identity under the Account.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Customer Personal Data:</strong> The personal data contained within the Customer Data, including any special categories of personal data defined under European Data Protection Law.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Data Incident:</strong> A breach of Google's security leading to the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to, Customer Data on systems managed by or otherwise controlled by Google.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading size="md" mb={3} color="brand.700">
                7. Data Security
              </Heading>
              <Text fontSize="md" lineHeight="tall" color="gray.700" mb={3}>
                Google will implement and maintain technical, organizational and physical measures to protect Customer Data against accidental or unlawful destruction, loss, alteration, unauthorized disclosure or access. The Security Measures include:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text fontSize="sm" color="gray.600">• Measures to encrypt Customer Data</Text>
                <Text fontSize="sm" color="gray.600">• Measures to help ensure ongoing confidentiality, integrity, availability and resilience</Text>
                <Text fontSize="sm" color="gray.600">• Measures to help restore timely access to Customer Data following an incident</Text>
                <Text fontSize="sm" color="gray.600">• Regular testing of effectiveness</Text>
              </VStack>
            </Box>

            <Box bg="blue.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="brand.500">
              <Heading size="sm" mb={3} color="brand.700">
                Important Notice
              </Heading>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                This is a summary of key sections. For the complete Cloud Data Processing Addendum, please visit{' '}
                <Link href="https://cloud.google.com/terms/data-processing-addendum" color="brand.600" isExternal>
                  Google Cloud Terms
                </Link>
                . Lake Lucien Surgery Center operates in compliance with all applicable data protection laws and regulations.
              </Text>
            </Box>

            <Box pt={6} borderTop="1px solid" borderColor="gray.200">
              <Text fontSize="sm" color="gray.500">
                This addendum applies to Lake Lucien Surgery Center's use of Google Cloud Platform services including Firebase Hosting and other cloud infrastructure services.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default DataProcessingAddendum;
